import React, { useState, useRef, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Tag } from 'lucide-react';
import api from '../utils/api';
import TerminalWindow from '../components/TerminalWindow';
import MarkdownEditor from '../components/MarkdownEditor';

const Dashboard = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPost, setCurrentPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    status: 'draft',
    category_id: ''
  });
  const [categoryForm, setCategoryForm] = useState({ name: '', id: null });
  const [categoryEditMode, setCategoryEditMode] = useState(false);
  const [output, setOutput] = useState(null);
  const inputRef = useRef(null);
  const [command, setCommand] = useState('');

  // Fetch posts and categories from backend
  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await api.get('/posts/all/');
      setPosts(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch (err) {
      setPosts([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get('/categories/');
      setCategories(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch (err) {
      setCategories([]);
    }
  };

  // Utility to render terminal-style headers
  function renderTerminalText(text) {
    return text.split('\n').map((line, idx) => {
      const trimmed = line.trimStart();
      if (trimmed.startsWith('#')) {
        return (
          <div key={idx} style={{ color: '#00ff66', fontSize: '1.3em', fontWeight: 700, margin: '0.2em 0' }}>
            {trimmed.replace(/^#+\s*/, '')}
          </div>
        );
      }
      // Handle empty lines by adding spacing
      if (line.trim() === '') {
        return <div key={idx} style={{ height: '1em', margin: '0.5em 0' }}></div>;
      }
      return <div key={idx} style={{ margin: '0.2em 0' }}>{line}</div>;
    });
  }

  const handleCommand = (cmd) => {
    const trimmed = cmd.trim();
    if (trimmed === 'help') {
      setOutput(
        <div className="mt-4 text-[#e5e5e5]">
          <div>Available commands:</div>
          <ul className="list-disc pl-6">
            <li><span className="text-[#00ff66]">help</span>: Show this help message</li>
            <li><span className="text-[#00ff66]">ls</span>: List all post slugs</li>
            <li><span className="text-[#00ff66]">cat ./posts/&lt;slug&gt;.md</span>: Show a preview of a post</li>
            <li><span className="text-[#00ff66]">clear</span>: Clear the output</li>
          </ul>
        </div>
      );
    } else if (trimmed === 'ls') {
      setOutput(
        <div className="mt-4 text-[#e5e5e5]">
          {posts.map(post => (
            <div key={post.slug}><span className="text-[#00ff66]">./posts/{post.slug}.md</span></div>
          ))}
        </div>
      );
    } else if (trimmed.startsWith('cat ./posts/') && trimmed.endsWith('.md')) {
      const slug = trimmed.replace('cat ./posts/', '').replace('.md', '');
      const post = posts.find(p => p.slug === slug);
      if (post) {
        setOutput(
          <div className="mt-4">
            <div className="flex flex-col gap-2 mb-2">
              <span className="text-2xl font-bold text-[#e5e5e5]">{post.title}</span>
              <span className="text-xs text-[#39c5bb]">{post.published_at}</span>
            </div>
            <div className="pl-2 text-[#e5e5e5] bg-[#181a1b] rounded p-4 text-base whitespace-pre-wrap">{renderTerminalText(post.excerpt)}</div>
          </div>
        );
      } else {
        setOutput(<div className="mt-4 text-[#ff5555]">No such post: {slug}</div>);
      }
    } else if (trimmed === 'clear') {
      setOutput(null);
    } else {
      setOutput(<div className="mt-4 text-[#ff5555]">Command not found: {cmd}</div>);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleCommand(command);
    setCommand('');
  };

  const handleCreateNew = () => {
    setIsCreating(true);
    setIsEditing(false);
    setCurrentPost(null);
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      status: 'draft',
      category_id: ''
    });
  };

  const handleEdit = (post) => {
    setIsEditing(true);
    setIsCreating(false);
    setCurrentPost(post);
    setFormData({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      status: post.status,
      category_id: post.category ? post.category.id : ''
    });
  };

  const handleCancel = () => {
    setIsCreating(false);
    setIsEditing(false);
    setCurrentPost(null);
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      status: 'draft',
      category_id: ''
    });
  };

  const handleSave = async () => {
    if (!formData.title.trim()) return;
    try {
      if (isCreating) {
        const res = await api.post('/posts/create/', {
          ...formData,
          category: formData.category_id || null,
        });
        setPosts([res.data, ...posts]);
      } else if (isEditing && currentPost) {
        const res = await api.put(`/posts/${currentPost.slug}/update/`, {
          ...formData,
          category: formData.category_id || null,
        });
        setPosts(posts.map(p => p.id === currentPost.id ? res.data : p));
      }
      handleCancel();
    } catch (err) {
      setOutput(<div className="mt-4 text-[#ff5555]">Failed to save post.</div>);
    }
  };

  const handleDelete = async (postId, slug) => {
    try {
      await api.delete(`/posts/${slug}/delete/`);
      setPosts(posts.filter(p => p.id !== postId));
    } catch (err) {
      setOutput(<div className="mt-4 text-[#ff5555]">Failed to delete post.</div>);
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (categoryEditMode) {
        await api.put(`/categories/${categoryForm.id}/`, { name: categoryForm.name });
        fetchCategories();
      } else {
        await api.post('/categories/', { name: categoryForm.name });
        fetchCategories();
      }
      setCategoryForm({ name: '', id: null });
      setCategoryEditMode(false);
    } catch (err) {
      setOutput(<div className="mt-4 text-[#ff5555]">Failed to save category.</div>);
    }
  };

  const handleCategoryDelete = async (id) => {
    try {
      await api.delete(`/categories/${id}/`);
      fetchCategories();
    } catch (err) {
      setOutput(<div className="mt-4 text-[#ff5555]">Failed to delete category.</div>);
    }
  };

  // Terminal theme layout
  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-start py-8 sm:py-12 px-2 sm:px-4 md:px-8 bg-[#181a1b]" dir="ltr">
      <TerminalWindow prompt="abdelhadi@blog:~$" className="w-full max-w-5xl">
        {/* Terminal window bar (interactive prompt) */}
        <form onSubmit={handleSubmit} className="flex flex-row items-center gap-2 mb-4">
          <input
            ref={inputRef}
            type="text"
            className="bg-transparent outline-none border-none text-[#00ff66] font-mono w-full text-base sm:text-lg min-w-0 focus:ring-0 focus:outline-none"
            value={command}
            onChange={e => setCommand(e.target.value)}
            autoFocus
            spellCheck={false}
            autoComplete="off"
            placeholder="Type a command (help, ls, cat ./posts/<slug>.md, clear)"
            style={{minWidth:0}}
          />
        </form>
        {/* Terminal content */}
        <div className="flex flex-col gap-6 font-mono animate-fadein text-left overflow-x-auto">
          {/* Output from prompt */}
          {output}
          {/* Stats */}
          <div className="text-sm text-[#39c5bb] mb-4">
            # Dashboard Stats: <span className="text-[#00ff66]">Posts: {posts.length}</span> | <span className="text-[#00ff66]">Published: {posts.filter(p => p.status === 'published').length}</span> | <span className="text-[#00ff66]">Drafts: {posts.filter(p => p.status === 'draft').length}</span> | <span className="text-[#00ff66]">Views: {posts.reduce((sum, p) => sum + (p.views || 0), 0)}</span> | <span className="text-[#00ff66]">Likes: {posts.reduce((sum, p) => sum + (p.likes || 0), 0)}</span>
          </div>
          {/* Category Management */}
          <div className="terminal-output text-[#e5e5e5] mb-4">
            <span className="terminal-prompt text-[#39c5bb]">abdelhadi@blog:~$</span> <span className="text-[#00ff66]">Category Management</span>
            <form
              onSubmit={handleCategorySubmit}
              className="flex flex-col sm:flex-row gap-2 mt-2"
            >
              <input
                type="text"
                value={categoryForm.name}
                onChange={e => setCategoryForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Category name"
                className="px-3 py-2 border border-[#39c5bb] rounded-xl w-full sm:w-auto bg-[#181a1b] text-[#e5e5e5]"
                required
              />
              <button type="submit" className="px-4 py-2 bg-[#39c5bb] text-[#181a1b] rounded-xl font-semibold w-full sm:w-auto">
                {categoryEditMode ? 'Update' : 'Add'}
              </button>
              {categoryEditMode && (
                <button type="button" onClick={() => { setCategoryEditMode(false); setCategoryForm({ name: '', id: null }); }} className="px-3 py-2 text-[#e5e5e5] w-full sm:w-auto">
                  Cancel
                </button>
              )}
            </form>
            <ul className="flex flex-col sm:flex-row flex-wrap gap-2 mt-2">
              {categories.map(cat => (
                <li key={cat.id} className="flex flex-col sm:flex-row items-start sm:items-center gap-2 bg-[#181a1b] px-3 py-1 rounded-xl mb-2 sm:mb-0">
                  <span>{cat.name}</span>
                  <div className="flex gap-2">
                    <button onClick={() => { setCategoryEditMode(true); setCategoryForm({ name: cat.name, id: cat.id }); }} className="text-[#39c5bb] text-xs">Edit</button>
                    <button onClick={() => handleCategoryDelete(cat.id)} className="text-[#ff5555] text-xs">Delete</button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          {/* Editor Section */}
          {(isCreating || isEditing) && (
            <div className="terminal-output text-[#e5e5e5] mb-4 bg-[#181a1b] p-6 rounded-xl">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
                <h2 className="text-xl sm:text-2xl font-bold text-[#e5e5e5]">
                  {isCreating ? 'Create New Post' : 'Edit Post'}
                </h2>
                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                  <button
                    onClick={handleCancel}
                    className="flex items-center justify-center px-4 py-2 text-[#e5e5e5] hover:text-[#00ff66] font-medium transition-colors duration-200"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="flex items-center justify-center px-4 sm:px-6 py-2 bg-[#39c5bb] text-[#181a1b] rounded-xl hover:bg-[#00ff66] transition-all duration-300 font-semibold shadow-lg hover:shadow-xl"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </button>
                </div>
              </div>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-[#e5e5e5] mb-2">Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-3 border border-[#39c5bb] rounded-2xl focus:ring-2 focus:ring-[#39c5bb] focus:border-transparent transition-all duration-200 text-lg bg-[#23272e] text-[#e5e5e5]"
                    placeholder="Title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#e5e5e5] mb-2">Excerpt</label>
                  <textarea
                    value={formData.excerpt}
                    onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-3 border border-[#39c5bb] rounded-2xl focus:ring-2 focus:ring-[#39c5bb] focus:border-transparent transition-all duration-200 resize-none bg-[#23272e] text-[#e5e5e5]"
                    placeholder="Excerpt"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#e5e5e5] mb-2">Content</label>
                  <MarkdownEditor
                    value={formData.content}
                    onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                    placeholder="Write your post content in Markdown..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#e5e5e5] mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    className="px-4 py-3 border border-[#39c5bb] rounded-2xl focus:ring-2 focus:ring-[#39c5bb] focus:border-transparent transition-all duration-200 bg-[#23272e] text-[#e5e5e5]"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#e5e5e5] mb-2">Category</label>
                  <select
                    value={formData.category_id || ''}
                    onChange={e => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                    className="px-4 py-3 border border-[#39c5bb] rounded-2xl focus:ring-2 focus:ring-[#39c5bb] focus:border-transparent transition-all duration-200 bg-[#23272e] text-[#e5e5e5]"
                  >
                    <option value="">No Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
          {/* Posts List */}
          <div className="terminal-output text-[#e5e5e5] mb-4">
            <span className="terminal-prompt text-[#39c5bb]">abdelhadi@blog:~$</span> <span className="text-[#00ff66]">Your Posts</span>
            {posts.length === 0 ? (
              <div className="p-8 text-center">No posts yet.</div>
            ) : (
              <div className="flex flex-col gap-4 mt-4">
                {posts.map((post) => (
                  <div key={post.id} className="flex flex-col gap-1 p-4 rounded-lg bg-[#181a1b]">
                    <div className="flex items-center gap-2">
                      <span className="terminal-prompt text-[#39c5bb]">abdelhadi@blog:~$</span>
                      <span className="text-[#00ff66]">cat ./posts/{post.slug}.md</span>
                    </div>
                    <span className="text-lg font-bold text-[#e5e5e5]">{post.title}</span>
                    <span className="text-xs text-[#39c5bb]">{post.published_at}</span>
                    <div className="text-[#e5e5e5]">{renderTerminalText(post.excerpt)}</div>
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => handleEdit(post)} className="text-[#39c5bb] hover:text-[#00ff66] text-xs">Edit</button>
                      <button onClick={() => handleDelete(post.id, post.slug)} className="text-[#ff5555] hover:text-[#ff8888] text-xs">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={handleCreateNew}
              className="mt-4 flex items-center px-4 py-2 bg-[#39c5bb] text-[#181a1b] rounded-xl hover:bg-[#00ff66] transition-all duration-300 font-semibold"
            >
              <Plus className="h-4 w-4 mr-2" /> New Post
            </button>
          </div>
        </div>
      </TerminalWindow>
      {/* Subtle background pattern */}
      <div className="pointer-events-none fixed inset-0 z-0 opacity-10" style={{background: 'radial-gradient(circle at 60% 40%, #00ff66 0%, transparent 60%)'}} />
      <style>{`
        @keyframes fadein { from { opacity: 0; transform: translateY(16px);} to { opacity: 1; transform: none; } }
        .animate-fadein { animation: fadein 0.7s cubic-bezier(.4,1.7,.6,.97) both; }
      `}</style>
    </div>
  );
};

export default Dashboard; 
